import { useHash } from "./useHash.ts";

// Hash format: "#path?key=value&...". The community selection lives in the
// `community` query param so navigation between tabs preserves it without
// touching localStorage (matching the SPA's existing storage-free posture).
export function splitHash(hash: string): { path: string; query: string } {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const q = raw.indexOf("?");
  if (q === -1) return { path: raw, query: "" };
  return { path: raw.slice(0, q), query: raw.slice(q + 1) };
}

export function getHashCommunity(hash: string): string | null {
  const { query } = splitHash(hash);
  if (!query) return null;
  const params = new URLSearchParams(query);
  const c = params.get("community");
  if (!c) return null;
  if (!/^\d+$/.test(c)) return null;
  return c;
}

export function getHashParam(hash: string, key: string): string | null {
  const { query } = splitHash(hash);
  if (!query) return null;
  return new URLSearchParams(query).get(key);
}

// Build a hash like "#path?community=12&extra=foo". Pass null/undefined to
// drop a key. Keys are emitted in insertion order so URLs stay stable.
export function buildHash(
  path: string,
  params: Record<string, string | null | undefined> = {},
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    sp.set(k, v);
  }
  const qs = sp.toString();
  const base = path.startsWith("#") ? path : `#${path}`;
  return qs ? `${base}?${qs}` : base;
}

// Helper for building a tab link that preserves the current community.
export function tabHref(
  path: string,
  community: string | null,
  extra: Record<string, string> = {},
): string {
  const params: Record<string, string | null> = {
    community,
    ...extra,
  };
  return buildHash(path, params);
}

export function useCommunity(): string | null {
  const hash = useHash();
  return getHashCommunity(hash);
}

// Replace the community query param in-place, preserving the current path
// + other params. Used by the selector.
export function setHashCommunity(value: string | null): void {
  if (typeof window === "undefined") return;
  const { path, query } = splitHash(window.location.hash);
  const sp = new URLSearchParams(query);
  if (value === null) sp.delete("community");
  else sp.set("community", value);
  const qs = sp.toString();
  window.location.hash = qs ? `${path}?${qs}` : path;
}
